/**
 * 모듈 숙련도 탭 표시 데이터 빌더 — 도메인 state(ModuleMasteryController + 정의 TSV) → View 데이터 변환(순수).
 * Phaser 비의존 — t()/formatBig(전역)만 사용. 단위 테스트 대상.
 */
import { t } from "../i18n/i18n.js";
import { evaluateModuleEffectStats, type ModuleEffectData } from "../modules/ModuleEffect.js";
import type { ThemeImageKey } from "../themes/imageKeys.js";
import type { ModuleMasteryController } from "../systems/ModuleMasteryController.js";
import {
  ALL_MODULE_MASTERY_DEFINITIONS,
  type ModuleMasteryBranch,
  type ModuleMasteryDefinition,
  type ModuleMasteryNodeDefinition,
} from "../data/moduleMasteryDefinitions.js";

/** 모듈 숙련도 1행 — 모듈별 진행/효과/분기 상태. */
export interface ModuleMasteryRowData {
  readonly moduleId: string;
  /** 현지화된 모듈 이름 (module.<id>.name). */
  readonly name: string;
  /** "숙련 Lv.N" 라벨. */
  readonly masteryLabel: string;
  /** 숙련도 XP progress bar 입력값. */
  readonly progress: ModuleMasteryProgressData;
  /** "효과: ..." 적용 중인 숙련 효과 요약. */
  readonly effectsLabel: string;
  /** 분기 상태 라벨. */
  readonly branchStatusLabel: string;
  /** 다음 목표 라벨. */
  readonly nextGoalLabel: string;
  /** 펼침 상세에 표시할 적용 중 숙련 효과 상세 라인. */
  readonly effectDetailLines: readonly string[];
  /** 펼침 상세 행 데이터. 각 적용 효과 행은 좌측 아이콘 iconKey를 가진다. */
  readonly effectDetailRows: readonly ModuleMasteryEffectDetailRowData[];
  /** 레벨 3 분기(A/B) 노드 보유 여부 — 분기 버튼 표시 게이트. */
  readonly hasBranch: boolean;
  /** 분기 임계 달성 + 미선택 상태일 때만 A/B 버튼 표시. */
  readonly canChooseBranch: boolean;
  /** 현재 선택 분기 (미선택=undefined). */
  readonly selectedBranch?: ModuleMasteryBranch;
  /** 선택 전 비교에 표시할 레벨 3 분기 후보 효과. */
  readonly branchOptions: readonly ModuleMasteryBranchOptionData[];
  /** 분기 A 버튼 라벨 (현지화된 분기 이름, mastery.node.<id>). 없으면 "A". */
  readonly branchAName: string;
  /** 분기 B 버튼 라벨. 없으면 "B". */
  readonly branchBName: string;
  /** 분기 A 노드 해금됨(임계 도달) 여부 — 버튼 활성 힌트(미해금이면 선택해도 효과 대기). */
  readonly branchAReady: boolean;
  /** 분기 B 노드 해금됨 여부. */
  readonly branchBReady: boolean;
}

export interface ModuleMasteryBranchOptionData {
  readonly branch: ModuleMasteryBranch;
  readonly name: string;
  readonly iconKey: ThemeImageKey;
  /** 예: "피해 ×1.06, 골드 획득 ×1.05". */
  readonly effectSummary: string;
  readonly ready: boolean;
  readonly selected: boolean;
}

export interface ModuleMasteryProgressData {
  readonly progressRatio: number;
  readonly progressPercentLabel: string;
  readonly currentXp: number;
  readonly currentLevelStartXp: number;
  readonly nextRequiredXp: number;
  readonly progressVisible: boolean;
}

export interface ModuleMasteryEffectDetailRowData {
  readonly text: string;
  readonly iconKey?: ThemeImageKey;
}

export interface ModuleMasteryTabData {
  readonly rows: readonly ModuleMasteryRowData[];
}

export interface BuildModuleMasteryDataOptions {
  /** effectDsl의 {level} 평가용 모듈 레벨 getter. 미주입 시 Lv.1 기준. */
  readonly moduleLevel?: (moduleId: string) => number;
}

/** 분기 노드가 임계 도달했는지(선택 무관, XP만) — 버튼 활성 힌트용. */
function branchThresholdMet(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
  branch: ModuleMasteryBranch,
): boolean {
  return nodes.some(
    (n) => n.branch === branch && mastery.xp(moduleId) >= n.requiredXp,
  );
}

function nodeThresholdMet(
  mastery: ModuleMasteryController,
  moduleId: string,
  node: ModuleMasteryNodeDefinition,
): boolean {
  return mastery.xp(moduleId) >= node.requiredXp;
}

/** 분기 버튼 라벨 — 해당 분기 노드의 현지화 이름(mastery.node.<id>). 노드 없으면 분기 글자 fallback. */
function branchName(nodes: readonly ModuleMasteryNodeDefinition[], branch: ModuleMasteryBranch): string {
  const node = nodes.find((n) => n.branch === branch);
  return node ? nodeName(node) : branch;
}

function nodeName(node: ModuleMasteryNodeDefinition): string {
  return t(`mastery.node.${node.id}`);
}

function nodeEffectSummary(node: ModuleMasteryNodeDefinition, moduleLevel: number): string {
  const stats = evaluateModuleEffectStats(node.effect, moduleLevel)
    .map((s) => `${t(`moduleStat.${s.key}`)} ${s.value}`)
    .join(", ");
  return stats || t("mastery.effects.none");
}

const SPREAD_EFFECT_KEYS: readonly (keyof ModuleEffectData)[] = [
  "splitCount",
  "pierce",
  "chain",
  "explosionRadius",
  "bounce",
  "goldMul",
  "markHits",
  "shockwaveRadius",
  "enhanceNextChance",
  "echoPrevChance",
  "echoNextChance",
];

function branchIconKey(effect: ModuleEffectData): ThemeImageKey {
  return SPREAD_EFFECT_KEYS.some((key) => effect[key] !== undefined)
    ? "icon_mastery_branch_spread"
    : "icon_mastery_branch_focus";
}

function effectDetailIconKey(node: ModuleMasteryNodeDefinition): ThemeImageKey {
  if (node.branch !== undefined) return branchIconKey(node.effect);
  return node.level <= 1 ? "icon_mastery_effect_stable" : "icon_mastery_effect_boost";
}

function masteryLevel(mastery: ModuleMasteryController, moduleId: string, nodes: readonly ModuleMasteryNodeDefinition[]): number {
  return nodes.reduce((max, node) => (nodeThresholdMet(mastery, moduleId, node) ? Math.max(max, node.level) : max), 0);
}

function effectsLabel(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
): string {
  const unlocked = mastery.unlockedNodes(moduleId, nodes).map(nodeName);
  return t("mastery.effects", {
    effects: unlocked.length > 0 ? unlocked.join(" · ") : t("mastery.effects.none"),
  });
}

function effectDetailRows(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
  moduleLevel: number,
): ModuleMasteryEffectDetailRowData[] {
  return mastery.unlockedNodes(moduleId, nodes).map((node) => {
    return {
      text: `${nodeName(node)}: ${nodeEffectSummary(node, moduleLevel)}`,
      iconKey: effectDetailIconKey(node),
    };
  });
}

function branchOptions(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
  moduleLevel: number,
): ModuleMasteryBranchOptionData[] {
  const selected = mastery.selectedBranch(moduleId);
  return nodes
    .filter((n): n is ModuleMasteryNodeDefinition & { readonly branch: ModuleMasteryBranch } => n.branch !== undefined)
    .sort((a, b) => a.branch.localeCompare(b.branch))
    .map((node) => ({
      branch: node.branch,
      name: nodeName(node),
      iconKey: branchIconKey(node.effect),
      effectSummary: nodeEffectSummary(node, moduleLevel),
      ready: nodeThresholdMet(mastery, moduleId, node),
      selected: selected === node.branch && nodeThresholdMet(mastery, moduleId, node),
    }));
}

export function hasSelectableModuleMasteryBranch(
  mastery: ModuleMasteryController,
  definitions: readonly ModuleMasteryDefinition[] = ALL_MODULE_MASTERY_DEFINITIONS,
): boolean {
  return definitions.some((def) => {
    if (mastery.selectedBranch(def.moduleId) !== undefined) return false;
    return (
      branchThresholdMet(mastery, def.moduleId, def.nodes, "A") ||
      branchThresholdMet(mastery, def.moduleId, def.nodes, "B")
    );
  });
}

function branchStatusLabel(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
): string {
  const selected = mastery.selectedBranch(moduleId);
  const selectedReady = selected ? branchThresholdMet(mastery, moduleId, nodes, selected) : false;
  const ready = branchThresholdMet(mastery, moduleId, nodes, "A") || branchThresholdMet(mastery, moduleId, nodes, "B");
  if (selected && selectedReady) return "";
  if (!selected && ready) return t("mastery.branchReady");
  return t("mastery.branchLocked", { level: 3 });
}

function isComplete(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
): boolean {
  const selected = mastery.selectedBranch(moduleId);
  const nonBranchDone = nodes
    .filter((n) => n.branch === undefined)
    .every((n) => nodeThresholdMet(mastery, moduleId, n));
  if (!nonBranchDone) return false;
  const branchNodes = nodes.filter((n) => n.branch !== undefined);
  if (branchNodes.length === 0) return true;
  return selected !== undefined && branchNodes.some((n) => n.branch === selected && nodeThresholdMet(mastery, moduleId, n));
}

function nextGoalLabel(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
): string {
  const selected = mastery.selectedBranch(moduleId);
  const branchReady = branchThresholdMet(mastery, moduleId, nodes, "A") || branchThresholdMet(mastery, moduleId, nodes, "B");
  if (!selected && branchReady) return t("mastery.nextGoal.branchReady");
  if (isComplete(mastery, moduleId, nodes)) return t("mastery.nextGoal.max");

  const candidates = nodes
    .filter((n) => !nodeThresholdMet(mastery, moduleId, n))
    .filter((n) => n.branch === undefined || selected === undefined || n.branch === selected)
    .sort((a, b) => a.level - b.level || a.requiredXp - b.requiredXp);
  const next = candidates[0];
  if (!next) return t("mastery.nextGoal.max");
  return t("mastery.nextGoal.level", { level: next.level });
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function progressPercentLabel(progressRatio: number): string {
  return `${Math.round(clamp01(progressRatio) * 100)}%`;
}

function progressData(
  mastery: ModuleMasteryController,
  moduleId: string,
  nodes: readonly ModuleMasteryNodeDefinition[],
): ModuleMasteryProgressData {
  const xp = mastery.xp(moduleId);
  const selected = mastery.selectedBranch(moduleId);
  const branchReady = branchThresholdMet(mastery, moduleId, nodes, "A") || branchThresholdMet(mastery, moduleId, nodes, "B");
  if ((!selected && branchReady) || isComplete(mastery, moduleId, nodes)) {
    return { progressRatio: 0, progressPercentLabel: "", currentXp: xp, currentLevelStartXp: 0, nextRequiredXp: 0, progressVisible: false };
  }
  const candidates = nodes
    .filter((n) => !nodeThresholdMet(mastery, moduleId, n))
    .filter((n) => n.branch === undefined || selected === undefined || n.branch === selected)
    .sort((a, b) => a.level - b.level || a.requiredXp - b.requiredXp);
  const next = candidates[0];
  if (!next) {
    return { progressRatio: 0, progressPercentLabel: "", currentXp: xp, currentLevelStartXp: 0, nextRequiredXp: 0, progressVisible: false };
  }
  const currentLevelStartXp = nodes
    .filter((n) => n.requiredXp < next.requiredXp && nodeThresholdMet(mastery, moduleId, n))
    .reduce((max, n) => Math.max(max, n.requiredXp), 0);
  const span = Math.max(1, next.requiredXp - currentLevelStartXp);
  const ratio = clamp01((xp - currentLevelStartXp) / span);
  return {
    progressRatio: ratio,
    progressPercentLabel: progressPercentLabel(ratio),
    currentXp: xp,
    currentLevelStartXp,
    nextRequiredXp: next.requiredXp,
    progressVisible: true,
  };
}

/**
 * 숙련도 탭 데이터 — 정의가 있는 모든 모듈을 순회(선언 순서). 보유/배치 여부와 무관하게 노출하되
 * XP는 컨트롤러 상태 기준(미사용 모듈은 0). unlocked 수는 selectedBranch 반영(분기 게이트).
 */
export function buildModuleMasteryData(
  mastery: ModuleMasteryController,
  opts: BuildModuleMasteryDataOptions = {},
): ModuleMasteryTabData {
  const rows = ALL_MODULE_MASTERY_DEFINITIONS.map((def): ModuleMasteryRowData => {
    const { moduleId, nodes } = def;
    const moduleLevel = opts.moduleLevel?.(moduleId) ?? 1;
    const hasBranch = nodes.some((n) => n.branch !== undefined);
    const branchReady = branchThresholdMet(mastery, moduleId, nodes, "A") || branchThresholdMet(mastery, moduleId, nodes, "B");
    const selectedBranch = mastery.selectedBranch(moduleId);
    const selectedBranchReady = selectedBranch
      ? branchThresholdMet(mastery, moduleId, nodes, selectedBranch)
      : false;
    const detailRows = effectDetailRows(mastery, moduleId, nodes, moduleLevel);
    return {
      moduleId,
      name: t(`module.${moduleId}.name`),
      masteryLabel: t("mastery.level", { level: masteryLevel(mastery, moduleId, nodes) }),
      progress: progressData(mastery, moduleId, nodes),
      effectsLabel: effectsLabel(mastery, moduleId, nodes),
      branchStatusLabel: branchStatusLabel(mastery, moduleId, nodes),
      nextGoalLabel: nextGoalLabel(mastery, moduleId, nodes),
      effectDetailLines: detailRows.map((row) => row.text),
      effectDetailRows: detailRows,
      hasBranch,
      canChooseBranch: hasBranch && branchReady && selectedBranch === undefined,
      ...(selectedBranch && selectedBranchReady ? { selectedBranch } : {}),
      branchOptions: branchOptions(mastery, moduleId, nodes, moduleLevel),
      branchAName: branchName(nodes, "A"),
      branchBName: branchName(nodes, "B"),
      branchAReady: branchThresholdMet(mastery, moduleId, nodes, "A"),
      branchBReady: branchThresholdMet(mastery, moduleId, nodes, "B"),
    };
  });
  return { rows };
}
